import { action, makeObservable } from 'mobx';

import { IRootStore } from '@stores/index';
import { apiAuthCaller } from '@services/apiCaller';
import { getApiErrorMessage } from '@components/common/util/util';

export enum AccountType {
  SOURCE = 'source',
  DESTINATION = 'destination',
  WHT = 'wht',
}

interface AccountCreatePayload {
  accountId?: string;
  accountInfo: {
    name?: string;
    options?: Record<string, unknown>;
    secret?: Record<string, unknown>;
    secretUpdated?: Record<string, unknown>;
  };
  role: string;
}

export interface IAccount {
  id: string;
  userId: string;
  role: string;
  skipValidation?: boolean;
  createdAt: string;
  updatedAt: string;
  name: string;
  options?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  workspaceId: string;
  rudderCategory?: string;
}

export interface IAccountsStore {
  fetchRetlAccounts: (roles: string) => Promise<IAccount[]>;
  fetchById: <T = IAccount>(accountId: string) => Promise<T | undefined>;
  createAccount: (payload: AccountCreatePayload) => Promise<IAccount | undefined>;
}

export class AccountsStore implements IAccountsStore {
  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  public async fetchRetlAccounts(roles: string) {
    const { messagesStore } = this.rootStore;

    try {
      const { data } = await apiAuthCaller().get<IAccount[]>(`/accounts`, {
        params: { roles, rudderCategory: AccountType.SOURCE },
      });

      return data;
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to fetch accounts'));
      return [];
    }
  }

  @action.bound
  public async fetchById<T = IAccount>(accountId: string) {
    const { messagesStore } = this.rootStore;

    try {
      const response = await apiAuthCaller().get<T>(`/accounts/${accountId}`);

      return response.data;
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to fetch accounts'));
      return undefined;
    }
  }

  @action.bound
  public async createAccount(payload: AccountCreatePayload) {
    const { messagesStore } = this.rootStore;

    try {
      const response = await apiAuthCaller().post<IAccount>('/warehouseSources/accounts', payload);

      return response.data;
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to create account'));
      return undefined;
    }
  }
}
